import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from './ProtectedRoute'
import { RedirectIfAuthenticated } from './RedirectIfAuthenticated'
import { RoleRoute } from './RoleRoute'
import { OnboardingGate } from './OnboardingGate'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { ExternalRedirect, BlogSlugRedirect, RootRedirect } from './redirects'
import { webUrl } from '@/constants/site'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { OperatorLayout } from '@/components/layout/OperatorLayout'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { UserLayout } from '@/components/layout/UserLayout'
import { ROUTES } from '@/constants/routes'

// ── Lazy-loaded pages ─────────────────────────────────────────

// Public (funnel deep-linked da www: le pagine SEO/marketing vivono su ollylife.com)
const OperatorProfilePage  = lazy(() => import('@/pages/public/OperatorProfilePage'))
const MatchingQuizPage     = lazy(() => import('@/pages/public/MatchingQuizPage'))

// Auth
const LoginChooserPage     = lazy(() => import('@/pages/auth/LoginChooserPage'))
const UserLoginPage        = lazy(() => import('@/pages/auth/UserLoginPage'))
const OperatorLoginPage    = lazy(() => import('@/pages/auth/OperatorLoginPage'))
const RegisterPage         = lazy(() => import('@/pages/auth/RegisterPage'))
const CallbackPage         = lazy(() => import('@/pages/auth/CallbackPage'))
const ForgotPasswordPage   = lazy(() => import('@/pages/auth/ForgotPasswordPage'))

// Operator
const OperatorDashboard      = lazy(() => import('@/pages/operator/DashboardPage'))
const ProfileWizardPage      = lazy(() => import('@/pages/operator/ProfileWizardPage'))
const GDPRConsentPage        = lazy(() => import('@/pages/operator/onboarding/GDPRConsentPage'))
const FiscalDataPage         = lazy(() => import('@/pages/operator/onboarding/FiscalDataPage'))
const SubmitApplicationPage  = lazy(() => import('@/pages/operator/onboarding/SubmitApplicationPage'))
const ContractAcceptancePage = lazy(() => import('@/pages/operator/onboarding/ContractAcceptancePage'))
const SubscriptionPaymentPage = lazy(() => import('@/pages/operator/onboarding/SubscriptionPaymentPage'))
const SubscriptionSuccessPage = lazy(() => import('@/pages/operator/onboarding/SubscriptionSuccessPage'))
const StripeConnectPage      = lazy(() => import('@/pages/operator/onboarding/StripeConnectPage'))
const StripeConnectReturnPage = lazy(() => import('@/pages/operator/onboarding/StripeConnectReturnPage'))
const BillingPage            = lazy(() => import('@/pages/operator/BillingPage'))
const ClientsPage            = lazy(() => import('@/pages/operator/ClientsPage'))
const OrdersPage             = lazy(() => import('@/pages/operator/OrdersPage'))
const InvoicesPage           = lazy(() => import('@/pages/operator/InvoicesPage'))
const PassiveInvoicesPage    = lazy(() => import('@/pages/operator/PassiveInvoicesPage'))
const FiscalDataEditPage     = lazy(() => import('@/pages/operator/FiscalDataEditPage'))
const ProfileEditPage        = lazy(() => import('@/pages/operator/ProfileEditPage'))
const ProfileSettingsPage    = lazy(() => import('@/pages/operator/ProfileSettingsPage'))
const GoogleCalendarReturnPage = lazy(() => import('@/pages/operator/GoogleCalendarReturnPage'))
const ServicesPage           = lazy(() => import('@/pages/operator/ServicesPage'))
const CalendarPage           = lazy(() => import('@/pages/operator/CalendarPage'))
const AvailabilityPage       = lazy(() => import('@/pages/operator/AvailabilityPage'))
const OperatorArticlesPage   = lazy(() => import('@/pages/operator/ArticlesPage'))
const OperatorArticleEditor  = lazy(() => import('@/pages/operator/ArticleEditorPage'))
const OperatorReviewsPage    = lazy(() => import('@/pages/operator/ReviewsPage'))
const OperatorBadgePage      = lazy(() => import('@/pages/operator/BadgePage'))
const OperatorMessagesPage   = lazy(() => import('@/pages/operator/MessagesPage'))
const OperatorConversationPage = lazy(() => import('@/pages/operator/ConversationPage'))
const OperatorColleaguesPage = lazy(() => import('@/pages/operator/ColleaguesPage'))
const OperatorColleagueConversationPage = lazy(() => import('@/pages/operator/ColleagueConversationPage'))
const OperatorReferralPage   = lazy(() => import('@/pages/operator/ReferralPage'))
const OperatorGuidePage      = lazy(() => import('@/pages/operator/GuidePage'))
const OperatorAnnouncementsPage = lazy(() => import('@/pages/operator/AnnouncementsPage'))

// Admin
const AdminDashboard         = lazy(() => import('@/pages/admin/DashboardPage'))
const ReviewProfilesPage     = lazy(() => import('@/pages/admin/ReviewProfilesPage'))
const OperatorsListPage      = lazy(() => import('@/pages/admin/OperatorsListPage'))
const OperatorDetailPage     = lazy(() => import('@/pages/admin/OperatorDetailPage'))
const AdminAppointmentsPage  = lazy(() => import('@/pages/admin/AppointmentsListPage'))
const AdminUsersListPage     = lazy(() => import('@/pages/admin/UsersListPage'))
const AdminUserDetailPage    = lazy(() => import('@/pages/admin/UserDetailPage'))
const AdminMatchingPage      = lazy(() => import('@/pages/admin/MatchingStatsPage'))
const InvitesPage            = lazy(() => import('@/pages/admin/InvitesPage'))
const AdminLeadsPage         = lazy(() => import('@/pages/admin/LeadsPage'))
const AdminEventsPage        = lazy(() => import('@/pages/admin/EventsPage'))
const AdminEventDetailPage   = lazy(() => import('@/pages/admin/EventDetailPage'))
const AdminAutomationsPage   = lazy(() => import('@/pages/admin/AutomationsPage'))
const AdminEmailLogPage      = lazy(() => import('@/pages/admin/EmailLogPage'))
const TemplatesPage          = lazy(() => import('@/pages/admin/TemplatesPage'))
const AdminAnnouncementsPage = lazy(() => import('@/pages/admin/AnnouncementsPage'))
const AdminSupportInboxPage  = lazy(() => import('@/pages/admin/SupportInboxPage'))
const AdminSupportConversationPage = lazy(() => import('@/pages/admin/SupportConversationPage'))
const AdminAccountingPage    = lazy(() => import('@/pages/admin/AccountingPage'))
const AdminArticlesListPage  = lazy(() => import('@/pages/admin/ArticlesListPage'))
const AdminArticleDetailPage = lazy(() => import('@/pages/admin/ArticleDetailPage'))
const AdminReviewArticlesPage = lazy(() => import('@/pages/admin/ReviewArticlesPage'))
const AdminReviewsModerationPage = lazy(() => import('@/pages/admin/ReviewsModerationPage'))

// User
const UserDashboardPage      = lazy(() => import('@/pages/user/DashboardPage'))
const UserSessionsPage       = lazy(() => import('@/pages/user/SessionsPage'))
const UserReviewsPage        = lazy(() => import('@/pages/user/ReviewsPage'))
const UserProfilePage        = lazy(() => import('@/pages/user/ProfilePage'))
const UserSavedPage          = lazy(() => import('@/pages/user/SavedPage'))
const UserMessagesPage       = lazy(() => import('@/pages/user/MessagesPage'))
const UserConversationPage   = lazy(() => import('@/pages/user/ConversationPage'))

// Videocall (USER o OPERATOR autorizzato dell'appointment)
const VideocallRoom          = lazy(() => import('@/pages/videocall/VideocallRoom'))

// ── Suspense wrapper ──────────────────────────────────────────

function SuspenseFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

// ── Route tree ────────────────────────────────────────────────

export function AppRoutes() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>

        {/* ── Funnel deep-linked da www (booking) ── */}
        <Route element={<PublicLayout />}>
          <Route path={ROUTES.OPERATOR_PROFILE} element={<OperatorProfilePage />} />
          <Route path={ROUTES.MATCHING}         element={<MatchingQuizPage />} />
        </Route>

        {/* ── Vecchi URL pubblici → redirect al sito www ───── */}
        <Route path={ROUTES.HOME}           element={<RootRedirect />} />
        <Route path={ROUTES.APPLY}          element={<ExternalRedirect to={webUrl('/diventa-operatore')} />} />
        <Route path={ROUTES.SEARCH}         element={<ExternalRedirect to={webUrl('/cerca')} />} />
        <Route path={ROUTES.BLOG}           element={<ExternalRedirect to={webUrl('/blog')} />} />
        <Route path={ROUTES.ARTICLE_DETAIL} element={<BlogSlugRedirect />} />
        <Route path={ROUTES.PRIVACY}        element={<ExternalRedirect to={webUrl('/privacy')} />} />
        <Route path={ROUTES.COOKIE_POLICY}  element={<ExternalRedirect to={webUrl('/cookie-policy')} />} />

        {/* ── Auth ───────────────────────────────────────── */}
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.LOGIN}           element={<RedirectIfAuthenticated><LoginChooserPage /></RedirectIfAuthenticated>} />
          <Route path={ROUTES.LOGIN_USER}      element={<RedirectIfAuthenticated><UserLoginPage /></RedirectIfAuthenticated>} />
          <Route path={ROUTES.LOGIN_OPERATOR}  element={<RedirectIfAuthenticated><OperatorLoginPage /></RedirectIfAuthenticated>} />
          <Route path={ROUTES.REGISTER}        element={<RegisterPage />} />
          <Route path={ROUTES.AUTH_CALLBACK}   element={<CallbackPage />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        </Route>

        {/* ── Operator ───────────────────────────────────── */}
        <Route
          path={ROUTES.OPERATOR.ROOT}
          element={
            <ProtectedRoute>
              <RoleRoute roles={['OPERATOR']}>
                <OnboardingGate>
                  <OperatorLayout />
                </OnboardingGate>
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={ROUTES.OPERATOR.DASHBOARD} replace />} />
          <Route path="dashboard"                              element={<OperatorDashboard />} />
          <Route path="onboarding/gdpr"                        element={<GDPRConsentPage />} />
          <Route path="onboarding/fiscal-data"                 element={<FiscalDataPage />} />
          <Route path="onboarding/submit"                      element={<SubmitApplicationPage />} />
          <Route path="onboarding/contract"                    element={<ContractAcceptancePage />} />
          <Route path="onboarding/subscription"                element={<SubscriptionPaymentPage />} />
          <Route path="onboarding/subscription/success"        element={<SubscriptionSuccessPage />} />
          <Route path="onboarding/stripe-connect"              element={<StripeConnectPage />} />
          <Route path="onboarding/stripe-connect/return"       element={<StripeConnectReturnPage />} />
          <Route path="billing"                                element={<BillingPage />} />
          <Route path="clients"                                element={<ClientsPage />} />
          <Route path="orders"                                 element={<OrdersPage />} />
          <Route path="invoices"                               element={<InvoicesPage />} />
          <Route path="passive-invoices"                       element={<PassiveInvoicesPage />} />
          <Route path="fiscal-data"                            element={<FiscalDataEditPage />} />
          <Route path="profile/wizard"                         element={<ProfileWizardPage />} />
          <Route path="profile/edit"                           element={<ProfileEditPage />} />
          <Route path="settings"                               element={<ProfileSettingsPage />} />
          <Route path="settings/google-calendar/return"        element={<GoogleCalendarReturnPage />} />
          <Route path="services"                               element={<ServicesPage />} />
          <Route path="calendar"                               element={<CalendarPage />} />
          <Route path="availability"                           element={<AvailabilityPage />} />
          <Route path="articles"                               element={<OperatorArticlesPage />} />
          <Route path="articles/new"                           element={<OperatorArticleEditor />} />
          <Route path="articles/:id/edit"                      element={<OperatorArticleEditor />} />
          <Route path="reviews"                                element={<OperatorReviewsPage />} />
          <Route path="badge"                                  element={<OperatorBadgePage />} />
          <Route path="messages"                               element={<OperatorMessagesPage />} />
          <Route path="messages/:convId"                       element={<OperatorConversationPage />} />
          <Route path="colleghi"                               element={<OperatorColleaguesPage />} />
          <Route path="colleghi/:convId"                       element={<OperatorColleagueConversationPage />} />
          <Route path="referrals"                              element={<OperatorReferralPage />} />
          <Route path="guide"                                  element={<OperatorGuidePage />} />
          <Route path="announcements"                          element={<OperatorAnnouncementsPage />} />
        </Route>

        {/* ── Admin ──────────────────────────────────────── */}
        <Route
          path={ROUTES.ADMIN.ROOT}
          element={
            <ProtectedRoute>
              <RoleRoute roles={['ADMIN']}>
                <AdminLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={ROUTES.ADMIN.DASHBOARD} replace />} />
          <Route path="dashboard"          element={<AdminDashboard />} />
          <Route path="reviews"            element={<ReviewProfilesPage />} />
          <Route path="reviews/moderation" element={<AdminReviewsModerationPage />} />
          <Route path="operators"          element={<OperatorsListPage />} />
          <Route path="operators/:id"      element={<OperatorDetailPage />} />
          <Route path="appointments"       element={<AdminAppointmentsPage />} />
          <Route path="users"              element={<AdminUsersListPage />} />
          <Route path="users/:id"          element={<AdminUserDetailPage />} />
          <Route path="matching"           element={<AdminMatchingPage />} />
          <Route path="articles"           element={<AdminArticlesListPage />} />
          <Route path="articles/reviews"   element={<AdminReviewArticlesPage />} />
          <Route path="articles/new"       element={<AdminArticleDetailPage />} />
          <Route path="articles/:id"       element={<AdminArticleDetailPage />} />
          <Route path="invites"            element={<InvitesPage />} />
          <Route path="leads"              element={<AdminLeadsPage />} />
          <Route path="eventi"             element={<AdminEventsPage />} />
          <Route path="eventi/:eventId"    element={<AdminEventDetailPage />} />
          <Route path="automations"        element={<AdminAutomationsPage />} />
          <Route path="email"              element={<AdminEmailLogPage />} />
          <Route path="templates"          element={<TemplatesPage />} />
          <Route path="announcements"      element={<AdminAnnouncementsPage />} />
          <Route path="supporto"           element={<AdminSupportInboxPage />} />
          <Route path="supporto/:convId"   element={<AdminSupportConversationPage />} />
          <Route path="accounting"         element={<AdminAccountingPage />} />
        </Route>

        {/* ── User ───────────────────────────────────────── */}
        <Route
          path={ROUTES.USER.ROOT}
          element={
            <ProtectedRoute>
              <RoleRoute roles={['USER']}>
                <UserLayout />
              </RoleRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={ROUTES.USER.DASHBOARD} replace />} />
          <Route path="dashboard" element={<UserDashboardPage />} />
          <Route path="sessions"  element={<UserSessionsPage />} />
          <Route path="reviews"   element={<UserReviewsPage />} />
          <Route path="saved"     element={<UserSavedPage />} />
          <Route path="profile"   element={<UserProfilePage />} />
          <Route path="messages"          element={<UserMessagesPage />} />
          <Route path="messages/:convId"  element={<UserConversationPage />} />
        </Route>

        {/* ── Videocall (USER o OPERATOR dell'appointment) ── */}
        <Route
          path={ROUTES.VIDEOCALL}
          element={
            <ProtectedRoute>
              <RoleRoute roles={['USER', 'OPERATOR']}>
                <VideocallRoom />
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* ── Catch-all (auth-aware: dashboard o www) ──────── */}
        <Route path="*" element={<RootRedirect />} />

      </Routes>
    </Suspense>
  )
}
